import { useSyncedRef, useToggle } from "@react-hookz/web";
import { useAtomValue, useSetAtom } from "jotai";
import { useRouter } from "next/router";
import qs from "qs";
import { useCallback, useEffect } from "react";
import { getTriggerPoint, NewsletterVariant, useNewsletterApi } from "scmp-sdk";

import { sendMirrorCustomEvent } from "shared/components/tracking/mirror";
import { useFallbackStorageValue, useParsedCookie } from "shared/hooks";
import { accountAtom } from "shared/lib/account/atoms";
import { useRouterReplaceWithSanitizedKeys } from "shared/lib/router";

import { useLoginDialogStateHelper } from "scmp-app/components/login-dialog/hooks";
import type { DataMatrix } from "scmp-app/components/newsletter/newsletter-widget-container/types";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { sendGTMTracking } from "scmp-app/components/tracking/google-tag-manager/apis";

import { signUpActionRedirectAtom } from "./atoms";
import {
  SanitizedUrlKeys,
  SignUpMatrixV1LocalStorageKey,
  SubscriptionComboLocalStorageKey,
} from "./consts";
import { shouldShowNewsletterSignUp } from "./helpers";
import type { NewsletterGASuccessParameter, NewsletterPayload } from "./types";

export const useNewsletterSignUpRedirect = () => {
  const { openLoginDialog } = useLoginDialogStateHelper();
  const { user } = useAtomValue(accountAtom);
  const setSignUpActionRedirectAtom = useSetAtom(signUpActionRedirectAtom);

  const { fetchNewsletterByIds, handleSubscribeNewsletter, isDataReady } = useNewsletterApi();
  const { handleNewsletterSuccessTracking } = useNewsletterTracking();
  const latestHandleSubscribeNewsletter = useSyncedRef(handleSubscribeNewsletter);
  const { pathname, query } = useRouter();
  const replace = useRouterReplaceWithSanitizedKeys();
  const [showPopup, setShowPopup] = useToggle(false);

  const { value: dataMatrix } = useFallbackStorageValue<DataMatrix>(SignUpMatrixV1LocalStorageKey);
  const { value: isCombo } = useFallbackStorageValue<boolean>(SubscriptionComboLocalStorageKey);

  const execute = useCallback(
    async (parsedQueryString: NewsletterPayload, useremail: string) => {
      // Resolve newsletter_name from ids to avoid any encoding issues from query
      const resolvedItems = await fetchNewsletterByIds(
        parsedQueryString.newsletterIds,
        parsedQueryString.newsletterIds.length,
      );
      const resolvedNewsletterName =
        (resolvedItems ?? [])
          .map(item => item?.name)
          .filter(Boolean)
          .join("|") ||
        parsedQueryString.newsletterName ||
        "";

      const response = await latestHandleSubscribeNewsletter.current(
        parsedQueryString.newsletterIds,
        useremail,
      );
      handleNewsletterSuccessTracking({
        eventCategory: dataMatrix?.metrics?.event_category,
        isCombo: isCombo ?? false,
        isHongKong25: dataMatrix?.metrics?.isHongKong25,
        payload: {
          ...parsedQueryString,
          newsletterName: resolvedNewsletterName,
          packageName: parsedQueryString.packageName,
        },
        subscriptions: response.entity?.subscriptions?.map(element => element?.entity) ?? [],
      });

      setSignUpActionRedirectAtom(state => ({ ...state, ready: true }));

      void replace(
        SanitizedUrlKeys,
        { pathname, query },
        {
          shallow: true,
        },
      );

      if (!response.errors?.length) {
        setShowPopup(true);
      }
    },
    [
      dataMatrix?.metrics?.event_category,
      dataMatrix?.metrics?.isHongKong25,
      fetchNewsletterByIds,
      handleNewsletterSuccessTracking,
      isCombo,
      latestHandleSubscribeNewsletter,
      pathname,
      query,
      replace,
      setShowPopup,
      setSignUpActionRedirectAtom,
    ],
  );

  const needToShow = shouldShowNewsletterSignUp();

  useEffect(() => {
    const parsedQueryString = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    }) as unknown as NewsletterPayload;

    if (!needToShow || !parsedQueryString) {
      setSignUpActionRedirectAtom(state => ({ ...state, ready: true }));
      return;
    }

    if (!user?.uuid) {
      openLoginDialog({
        destination: window.location.href,
        ga4CustomParameter: {
          newsletter_name: parsedQueryString?.newsletterName ?? "",
          ...(parsedQueryString.packageName && { package_name: parsedQueryString.packageName }),
          trigger_point: "newsletter",
        },
      });
      return;
    }

    if (!isDataReady) return;
    void navigator.locks.request("newsletterSignUpRedirect", { ifAvailable: true }, async lock => {
      if (!lock) return;
      await execute(parsedQueryString, user.useremail);
    });
  }, [
    execute,
    isDataReady,
    needToShow,
    openLoginDialog,
    setSignUpActionRedirectAtom,
    user?.useremail,
    user?.uuid,
  ]);

  return {
    setShowPopup,
    showPopup,
  };
};

export const useNewsletterTracking = () => {
  const { value: loginType } = useParsedCookie("scmp_user_login_type", raw => raw);

  const handleNewsletterSuccessTracking = useCallback(
    (parameter: NewsletterGASuccessParameter) => {
      const sanitizedLoginType = loginType ?? "";
      if (parameter.payload.packageName) {
        sendGA4Tracking({
          action: "sys",
          category: "nl",
          customized_parameters: {
            internal_campaign: parameter.payload.campaignName ?? "",
            method: sanitizedLoginType.toLowerCase(),
            newsletter_name: parameter.payload.newsletterName,
            ...(parameter.payload.packageName && {
              package_name: parameter.payload.packageName,
            }),
            /**
             * https://docs.google.com/spreadsheets/d/1isYXvq5eNAwT75bHuYzMN947nBwxmUURziy-LdExT24/edit?gid=0#gid=0
             * the trigger point for `nl_sign_up_sys` event is different from `login_result_sys`
             * we use variant to determine the trigger point as the first priority
             */
            trigger_point:
              getTriggerPoint(parameter.payload.variant) ?? parameter.payload.triggerPoint,
          },
          subcategory: "sign_up",
        });
        sendGTMTracking({
          event: "newsletter-subscribe",
          "newsletter-id": parameter.payload.newsletterIds.toString() ?? "",
          "newsletter-name": parameter.payload.newsletterName ?? "",
        });
        sendMirrorCustomEvent({
          event_name: "newsletter_sign_up",
          newsletter_subscribe: parameter.payload.newsletterName ?? "",
        });
        return;
      }
      parameter.payload.newsletterIds.forEach((id, index) => {
        const data = parameter.subscriptions?.find(element => element?.entityId === id);
        const handleTrackGA4 = () => {
          if (parameter.payload.campaignName) {
            // Send once for acquisition newsletter popup
            const isRequireToSendAcquisitionPopupEvent =
              parameter.payload.variant === NewsletterVariant.AcquisitionNewsletterPopup &&
              index === 0;
            if (isRequireToSendAcquisitionPopupEvent) {
              sendGA4Tracking({
                action: "sys",
                category: "nl",
                customized_parameters: {
                  internal_campaign: parameter.payload.campaignName ?? "",
                  method: sanitizedLoginType.toLowerCase(),
                  newsletter_name: parameter.payload.newsletterName,
                  trigger_point: "ppto",
                },
                subcategory: "sign_up",
              });
            }
            return;
          }
          sendGA4Tracking({
            action: "sys",
            category: "nl",
            customized_parameters: {
              internal_campaign: parameter.payload.campaignName ?? "",
              method: sanitizedLoginType.toLowerCase(),
              newsletter_name: parameter.payload.newsletterName,
              ...(parameter.payload.packageName && { package_name: parameter.payload.packageName }),
              /**
               * https://docs.google.com/spreadsheets/d/1isYXvq5eNAwT75bHuYzMN947nBwxmUURziy-LdExT24/edit?gid=0#gid=0
               * the trigger point for `nl_sign_up_sys` event is different from `login_result_sys`
               * we use variant to determine the trigger point as the first priority
               */
              trigger_point:
                getTriggerPoint(parameter.payload.variant) ?? parameter.payload.triggerPoint,
            },
            subcategory: "sign_up",
          });
        };
        handleTrackGA4();
        sendGTMTracking({
          event: "newsletter-subscribe",
          "newsletter-id": data?.entityId ?? "",
          "newsletter-name": data?.entityLabel ?? "",
        });
        sendMirrorCustomEvent({
          event_name: "newsletter_sign_up",
          newsletter_subscribe: data?.entityLabel ?? "",
        });
      });
    },
    [loginType],
  );

  return {
    handleNewsletterSuccessTracking,
  };
};
